/**
 * HelpModalController - Handles help system modal and tips
 * Extracted from main.ts for better organization
 */

import { GameState } from '../core/GameState';
import { UIManager } from './UIManager';
import { 
  GAMEPLAY_TIPS, 
  REFERENCES, 
  GAMEPLAY_WALKTHROUGH, 
  PROGRESSION_LEVELS, 
  getRandomTip,
  getCurrentLevel,
} from '../data/HelpContent';

/** Tip rotation interval in milliseconds */
const TIP_ROTATION_INTERVAL_MS = 30000;

/**
 * HelpModalController manages the help system
 */
export class HelpModalController {
  private gameState: GameState;
  private ui: UIManager;
  private tipIntervalId: number | null = null;
  
  constructor(gameState: GameState, ui: UIManager) {
    this.gameState = gameState;
    this.ui = ui;
  }
  
  /**
   * Setup help system event listeners and initial state
   */
  setup(): void {
    // Show initial random tip
    this.showRandomTip();

    // Rotate tips every 30 seconds
    this.tipIntervalId = window.setInterval(() => this.showRandomTip(), TIP_ROTATION_INTERVAL_MS);

    // Update layout first
    this.updateHeaderGlow();
    this.updateLeftPanelLayout();

    // Use event delegation on left panel to catch clicks on help elements
    const leftPanel = this.ui.leftPanel;
    if (leftPanel) {
      leftPanel.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.id === 'help-header' || target.id === 'help-more-link' ||
            target.closest('#help-header') || target.closest('#help-more-link')) {
          e.preventDefault();
          this.showModal();
        }
      });
    }

    // Close help modal
    document.getElementById('help-modal-close')?.addEventListener('click', () => this.closeModal());
    this.ui.helpModal?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) {
        this.closeModal();
      }
    });
    
    // Subscribe to state changes to update UI when level changes
    this.gameState.subscribe('help-panel-layout', () => {
      this.updateHeaderGlow();
      this.updateLeftPanelLayout();
    });
  }
  
  /**
   * Update the help header glow effect for new players
   */
  updateHeaderGlow(): void {
    const header = this.ui.helpHeader;
    if (!header) return;
    
    // Show glow for new players (no missions completed, starting balance intact)
    const isNewPlayer = this.gameState.data.missionsCompleted === 0 && 
                        this.gameState.data.balance >= 50000000;
    
    if (isNewPlayer) {
      header.classList.add('new-player-glow');
    } else {
      header.classList.remove('new-player-glow');
    }
  }
  
  /**
   * Update left panel layout based on player level
   */
  updateLeftPanelLayout(): void {
    const level = getCurrentLevel(this.gameState.data.missionsCompleted).id;
    
    const techSection = this.ui.techTreeSection;
    const assetsSection = this.ui.assetsSection;
    const storageSection = this.ui.storageSection;
    
    if (level === 1) {
      // Level 1: Just hide tech/assets/storage - help stays in original position
      if (techSection) techSection.style.display = 'none';
      if (assetsSection) assetsSection.style.display = 'none';
      if (storageSection) storageSection.style.display = 'none';
    } else {
      // Level 2+: Show appropriate sections
      if (techSection) techSection.style.display = 'block';
      if (assetsSection) assetsSection.style.display = 'block';
      if (level >= 3 && storageSection) {
        storageSection.style.display = 'block';
      } else if (storageSection) {
        storageSection.style.display = 'none';
      }
    }
  }

  /**
   * Show a random gameplay tip
   */
  showRandomTip(): void {
    const tipEl = this.ui.helpTipText;
    if (tipEl) {
      const tip = getRandomTip();
      tipEl.innerHTML = tip.text;
    }
  }

  /**
   * Show the help modal with populated content
   */
  showModal(): void {
    const modal = this.ui.helpModal;
    const walkthrough = this.ui.helpWalkthrough;
    const progressionList = this.ui.helpProgressionList;
    const tipsList = this.ui.helpTipsList;
    const refsList = this.ui.helpReferencesList;

    if (!modal) return;

    // Populate walkthrough (convert markdown-style to HTML)
    if (walkthrough) {
      walkthrough.innerHTML = GAMEPLAY_WALKTHROUGH
        .split('\n\n')
        .map(para => {
          // Convert **text** to <strong>text</strong>
          para = para.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
          return `<p>${para}</p>`;
        })
        .join('');
    }

    // Populate progression levels
    if (progressionList) {
      const currentLevel = getCurrentLevel(this.gameState.data.missionsCompleted);
      progressionList.innerHTML = PROGRESSION_LEVELS
        .map(level => `
          <div class="progression-level ${level.id === currentLevel.id ? 'current' : ''}">
            <div class="progression-level-header">
              <span class="progression-level-name">${level.name}</span>
              <span class="progression-level-range">${level.contractRange}</span>
            </div>
            <div class="progression-level-desc">${level.description}</div>
            <div class="progression-level-goal">Goal: ${level.goal}</div>
          </div>
        `)
        .join('');
    }

    // Populate tips
    if (tipsList) {
      tipsList.innerHTML = GAMEPLAY_TIPS
        .map(tip => `<li>${tip.text}</li>`)
        .join('');
    }

    // Populate references
    if (refsList) {
      refsList.innerHTML = REFERENCES
        .map(ref => `
          <li>
            <a href="${ref.url}" target="_blank" rel="noopener noreferrer" class="help-reference-link">${ref.title}</a>
            ${ref.description ? `<div class="help-reference-desc">${ref.description}</div>` : ''}
          </li>
        `)
        .join('');
    }

    modal.classList.add('visible');
  }

  /**
   * Close the help modal
   */
  closeModal(): void {
    this.ui.hideModal(this.ui.helpModal);
  }
  
  /**
   * Cleanup interval on destroy
   */
  destroy(): void {
    if (this.tipIntervalId !== null) {
      clearInterval(this.tipIntervalId);
      this.tipIntervalId = null;
    }
  }
}
